// Conversation observability DTOs. Backs the dashboard "Conversations" page
// (Phase 5.3): a paginated list of recent threads + an expandable trace per
// conversation that ties messages to the ai_call_logs row that produced them.
//
// Shape choices:
//   • Summary carries the aggregate counters (messageCount, totalCostUsd) so
//     the list view doesn't have to fan out one trace request per row.
//   • Trace messages omit `tenantId` — RLS already gates the route, and the
//     dashboard renders inside a known tenant context.
//   • Each call-log row optionally references the assistant message it
//     produced (messageId). The dashboard zips them together in the UI.
import { Type } from '@sinclair/typebox';
import { ChatRoleSchema, ToolCallSchema } from './chat.js';
import { ProviderSchema, FinishReasonSchema } from './providers.js';
export const ConversationSummarySchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    agentId: Type.String({ format: 'uuid' }),
    // Joined from agents.name. May be null when the agent has been hard-deleted
    // (cascade catches conversation rows but `agents` left a hole until then).
    agentName: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()]),
    title: Type.Union([Type.String({ maxLength: 512 }), Type.Null()]),
    // Widget-bound conversations carry the (opaque) session JTI; null when the
    // conversation came from an SDK/API-key caller.
    externalSessionId: Type.Union([Type.String({ maxLength: 128 }), Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
    messageCount: Type.Integer({ minimum: 0 }),
    // Summed across ai_call_logs for this conversation. Returned as a number
    // (Drizzle stringifies `numeric`; the route casts before sending).
    totalCostUsd: Type.Number({ minimum: 0 }),
}, { $id: 'ConversationSummary', additionalProperties: false });
export const ConversationListResponseSchema = Type.Object({
    conversations: Type.Array(ConversationSummarySchema),
}, { $id: 'ConversationListResponse', additionalProperties: false });
// Trace message: row from `messages`. Mirrors ChatMessage where it overlaps
// (role, content, toolCalls, toolCallId, name) and adds the persistence-level
// fields the UI needs (id, sequence, finishReason, createdAt).
export const ConversationTraceMessageSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    sequence: Type.Integer({ minimum: 1 }),
    role: ChatRoleSchema,
    content: Type.String({ maxLength: 200_000 }),
    toolCalls: Type.Optional(Type.Array(ToolCallSchema, { maxItems: 16 })),
    toolCallId: Type.Optional(Type.Union([Type.String({ maxLength: 128 }), Type.Null()])),
    name: Type.Optional(Type.Union([Type.String({ maxLength: 128 }), Type.Null()])),
    finishReason: Type.Union([FinishReasonSchema, Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
}, { $id: 'ConversationTraceMessage', additionalProperties: false });
// Trace call-log: one row per provider call. The dashboard renders these next
// to (or inside) the assistant message that produced them — `messageId` is the
// join key. Older rows may have null messageId (cascade or pre-Phase-2 data).
export const ConversationTraceCallLogSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    requestId: Type.String({ minLength: 1, maxLength: 128 }),
    messageId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    provider: ProviderSchema,
    model: Type.String({ minLength: 1, maxLength: 128 }),
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    costUsd: Type.Number({ minimum: 0 }),
    latencyMs: Type.Integer({ minimum: 0 }),
    finishReason: Type.Union([FinishReasonSchema, Type.Null()]),
    // Canonical ErrorCode when the call failed. Null on success.
    errorClass: Type.Union([Type.String({ maxLength: 64 }), Type.Null()]),
    retries: Type.Integer({ minimum: 0 }),
    fallbackTriggered: Type.Boolean(),
    ts: Type.String({ format: 'date-time' }),
}, { $id: 'ConversationTraceCallLog', additionalProperties: false });
export const ConversationTraceSchema = Type.Object({
    conversation: ConversationSummarySchema,
    messages: Type.Array(ConversationTraceMessageSchema),
    callLogs: Type.Array(ConversationTraceCallLogSchema),
}, { $id: 'ConversationTrace', additionalProperties: false });
//# sourceMappingURL=conversations.js.map